<template>
  <div class="m-find-store find-store">
    <main class="find-store-cont">
      <div class="map-wrap" :class="{ 'is-far': mapZoom < labelMinZoom }">
        <!-- 네이버 지도가 안쪽을 직접 그리므로 버튼은 지도 영역 밖(형제)에 둔다 -->
        <section class="map-section" id="map" ref="mapZone"></section>

        <div class="map-zoom">
          <button type="button" aria-label="지도 확대" @click="zoomBy(1)">+</button>
          <button type="button" aria-label="지도 축소" @click="zoomBy(-1)">−</button>
        </div>

        <button
            type="button"
            class="my-location-btn"
            :class="{ 'is-large': !hasLocation }"
            :disabled="locating"
            @click="onMyLocation"
        >
          <svg class="my-location-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <circle cx="12" cy="12" r="8" fill="none" />
            <path d="M12 1v3M12 20v3M1 12h3M20 12h3" />
          </svg>
          <span v-if="locating">위치 확인 중</span>
          <span v-else-if="hasLocation">내 위치</span>
          <span v-else>내 위치로 가까운 매장 찾기</span>
        </button>
      </div>
      <section class="find-section">
        <ul class="find-btns"
            @wheel.prevent
            @touchmove.prevent
        >
          <li class="find-btn" :class="{ active: activeTab === 'nearby' }">
            <button type="button" @click="changeTab('nearby')">주변에서 찾기</button>
          </li>
          <li class="find-btn" :class="{ active: activeTab === 'search' }">
            <button type="button" @click="changeTab('search')">검색해서 찾기</button>
          </li>
        </ul>
        <!--./find button-->
        <div class="find-items">
          <!-- 주변에서 찾기 -->
          <div class="find-item nearby" :class="{ display: activeTab === 'nearby' }">
            <div
                class="find-result-comment"
                :class="{ 'is-message': loadError }"
            >
              <p @wheel.prevent @touchmove.prevent>
                <template v-if="loadError">매장 정보를 불러오지 못했어요. 잠시 후 다시 시도해주세요.</template>
                <span v-else-if="locationDenied && !hasLocation" class="sort-label is-hint">위치 허용 시 내 위치 기준 가까운 순으로 표시됩니다</span>
                <template v-else>
                  <em>총 {{ nearbyList.length }}개</em> 매장
                  <span v-if="hasLocation" class="sort-label">· 가까운 순
                    <button
                        type="button"
                        class="sort-help"
                        aria-label="정렬 기준 안내"
                        @click.stop="tipOpen = !tipOpen"
                    >?<span class="sort-tip" :class="{ show: tipOpen }" role="tooltip">직선거리 기준입니다</span></button>
                  </span>
                  <span v-else class="sort-label">· 가나다순</span>
                </template>
              </p>
            </div>
            <div class="find-result-wrap">
              <div class="find-result">
                <show-store
                    v-for="item in nearbyList"
                    :key="item.id"
                    :mapDataItem="item"
                    :selected="item.id === focusedId"
                    :distanceKm="hasLocation ? distanceById[item.id] : null"
                    v-on:moveLocation="selectStore"
                ></show-store>
              </div>
            </div>
          </div>

          <!-- 검색해서 찾기 -->
          <div class="find-item search" :class="{ display: activeTab === 'search' }">
            <p class="search-input">
              <input
                  ref="searchInput"
                  v-model="searchText"
                  id="search-store"
                  type="search"
                  placeholder="주소 또는 매장명으로 검색해보세요."
                  @keyup.enter="onSearchEnter"
                  @compositionstart="searchComposing = true"
                  @compositionend="searchComposing = false"
                  @blur="searchComposing = false"
                  @wheel.prevent
                  @touchmove.prevent
              /><img
                src="https://img.davich.com/image/sub/icon-search.png"
                alt="search icon"
                @click="commitSearch"
            />
            </p>
            <div class="find-result-comment">
              <p @wheel.prevent @touchmove.prevent>
                <em>총 {{ searchList.length }}개</em> 매장
                <span v-if="hasLocation" class="sort-label">· 가까운 순
                  <button
                      type="button"
                      class="sort-help"
                      aria-label="정렬 기준 안내"
                      @click.stop="tipOpen = !tipOpen"
                  >?<span class="sort-tip" :class="{ show: tipOpen }" role="tooltip">직선거리 기준입니다</span></button>
                </span>
                <span v-else class="sort-label">· 가나다순</span>
              </p>
            </div>
            <div class="find-result-wrap">
              <div class="find-result">
                <show-store
                    v-for="item in searchList"
                    :key="item.id"
                    :mapDataItem="item"
                    :selected="item.id === focusedId"
                    :distanceKm="hasLocation ? distanceById[item.id] : null"
                    v-on:moveLocation="selectStore"
                ></show-store>
              </div>
            </div>
          </div>
        </div>
        <!--./find-items-->

        <!-- 매장 요약 (목록 자리를 덮는 하단 시트) -->
        <div
          v-if="sheetOpen && focusedStore"
          class="store-sheet"
          role="dialog"
          aria-label="매장 정보"
        >
          <button
              type="button"
              class="store-sheet-back"
              :class="{ 'is-change': showChangeStoreLabel }"
              @click="backOrClose('sheet')"
          >
            <svg class="store-sheet-back-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
            <span>{{ showChangeStoreLabel ? "다른 매장 선택하기" : "목록으로" }}</span>
          </button>
          <div class="store-sheet-body">
            <div class="store-sheet-title">
              <h3>{{ focusedStore.name }}</h3>
              <span v-if="focusedDistance" class="distance">{{ focusedDistance }}</span>
            </div>
            <!-- 정보 줄 모양은 상세(MDetailStore)의 .store-info 와 같이 쓴다 -->
            <ul class="store-info">
              <li>
                <img class="store-info-icon" src="@/assets/icons/map@2x-100.jpg" alt="주소" />
                <span class="store-info-text">{{ focusedStore.addr }}</span>
                <button type="button" class="store-info-copy" @click="copyAddress(focusedStore.addr)">주소복사</button>
              </li>
              <li v-if="focusedStore.tel">
                <img class="store-info-icon" src="@/assets/icons/call@2x-100.jpg" alt="전화번호" />
                <span class="store-info-text">{{ focusedStore.tel }}</span>
              </li>
              <li v-if="focusedStore.workHour">
                <img class="store-info-icon" src="@/assets/icons/home@2x-100.jpg" alt="영업시간" />
                <span class="store-info-text">{{ focusedStore.workHour }}</span>
              </li>
            </ul>
            <button type="button" class="store-sheet-more" @click="openDetail(focusedStore)">
              <span>사진·상세 정보 보기</span>
              <svg class="store-sheet-more-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <!-- 신청서에서 고를 때: [이 매장 선택] 하나 -->
          <div v-if="isPicker" class="kakao-reserv-btns store-sheet-btns">
            <button type="button" class="pick-btn" @click="pick(focusedStore)">이 매장 선택</button>
          </div>
          <div v-else class="kakao-reserv-btns store-sheet-btns">
            <router-link :to="consultLink(focusedStore)" class="kakao-btn">
              <div class="store-icon-wrap kakao-icon-wrap kakaoIcon">
                <img src="https://img.davich.com/mobile/image/sub/support/map/icon-store-all-01.png" alt="" />
              </div>
              <span>상담예약</span>
            </router-link>
            <a
                v-if="focusedStore.naverUrl"
                :href="focusedStore.naverUrl"
                target="_blank"
                class="visit-btn"
            >
              <div class="store-icon-wrap reserv-icon-wrap reservIcon">
                <img src="https://img.davich.com/mobile/image/sub/support/map/icon-store-all-02.png" alt="" />
              </div>
              <span>방문예약</span>
            </a>
            <router-link v-else :to="consultLink(focusedStore)" class="visit-btn">
              <div class="store-icon-wrap reserv-icon-wrap reservIcon">
                <img src="https://img.davich.com/mobile/image/sub/support/map/icon-store-all-02.png" alt="" />
              </div>
              <span>방문예약</span>
            </router-link>
          </div>
        </div>
      </section>

      <!-- 매장 상세 -->
      <aside class="detail-section" :class="{ out: !detailOpen }" id="detail-section">
        <button type="button" class="detail-btn" @click="backOrClose('detail')">
          <img
              src="https://img.davich.com/mobile/image/sub/support/icon-del-btn.png"
              alt="delete btn"
          />
        </button>
        <detail-store
            :key="'store-detail-' + detailRenderKey"
            :store-props="storeProps"
            :pick-mode="isPicker"
            @pick="pick(focusedStore)"
        ></detail-store>
      </aside>
    </main>
  </div>
</template>

<script>
import ShowStore from "@/components/SUPPORT/MShowStore.vue";
import DetailStore from "@/components/SUPPORT/MDetailStore.vue";
import {
  HQ,
  MAP_ZOOM,
  MAP_BOTTOM_RESERVE,
  consultationLink,
  copyAddress,
  distanceKm,
  formatKm,
  getMockLocation,
  isMobileView,
  isStoreRowList,
  locationErrorMessage,
  locationPermissionState,
  normalizeForSearch,
  sortByNameKo,
  storeMarkerIcon,
  toDetailProps,
  emptyDetailProps,
} from "@/utils/storeFinder";
import storeMapMixin from "@/mixins/storeMapMixin";
import { lockPageScroll, unlockPageScroll } from "@/utils/scrollLock";

export default {
  name: "StoreFinder",
  components: { ShowStore, DetailStore },
  // 지도·핀·묶음 로직은 신청서 매장 고르기와 같이 쓴다
  mixins: [storeMapMixin],
  props: {
    // "page": 매장찾기 페이지 / "picker": 신청서에서 매장 고르기 (시트·상세 버튼이 [이 매장 선택] 하나)
    mode: { type: String, default: "page" },
    // picker: 이미 고른 매장 코드 (열면 그 매장을 보여준다)
    initialStoreCode: { type: String, default: "" },
  },
  data() {
    return {
      nowLat: null,
      nowLng: null,
      locating: false,
      locationDenied: false,
      loadError: false,
      activeTab: "nearby",
      // 사용자가 직접 탭을 누른 뒤에는 자동으로 탭을 바꾸지 않는다
      tabTouched: false,
      searchText: "",
      searchComposing: false, // 한글 조합 중인지 (조합 중에는 blur 하지 않는다)
      focusedId: null,
      // 목록 자리를 덮는 매장 요약 시트
      sheetOpen: false,
      detailOpen: false,
      storeProps: emptyDetailProps(),
      /** 상세 패널 강제 재마운트(클릭 매장별 텍스트·이미지 반영) */
      detailRenderKey: 0,
      // "가까운 순 ?" 안내 말풍선 (폰은 눌러서, PC는 올려서)
      tipOpen: false,
    };
  },
  computed: {
    // App.vue가 받아둔 매장 데이터를 같이 쓴다 (예전 형식 캐시는 제외)
    storeRows() {
      const rows = this.$store.state.support.storeData;
      return isStoreRowList(rows) ? rows : [];
    },
    isPicker() {
      return this.mode === "picker";
    },
    // 신청서에서 매장을 고르는 중에는 "목록으로" 대신 "다른 매장 선택하기"
    showChangeStoreLabel() {
      return this.isPicker;
    },
    hasLocation() {
      return this.nowLat != null && this.nowLng != null;
    },
    distanceById() {
      const d = {};
      if (!this.hasLocation) return d;
      this.storeRows.forEach((s) => {
        d[s.id] = distanceKm(this.nowLat, this.nowLng, s.lat, s.lng);
      });
      return d;
    },
    storesByDistance() {
      const d = this.distanceById;
      const dist = (s) => (d[s.id] != null ? d[s.id] : Infinity);
      return this.storeRows.slice().sort((a, b) => dist(a) - dist(b));
    },
    storesByName() {
      return sortByNameKo(this.storeRows);
    },
    // 위치 허용: 가까운 순(반경 제한 없음) / 미허용: 가나다순
    sortedStores() {
      return this.hasLocation ? this.storesByDistance : this.storesByName;
    },
    nearbyList() {
      return this.sortedStores;
    },
    // 검색해서 찾기: 시·도 표기를 통일해 비교
    searchList() {
      const q = normalizeForSearch(this.searchText);
      return q ? this.sortedStores.filter((s) => s.searchKey.includes(q)) : this.sortedStores;
    },
    // 지금 고른 매장 (요약 시트에 표시)
    focusedStore() {
      if (this.focusedId == null) return null;
      return this.storeRows.find((s) => s.id === this.focusedId) || null;
    },
    focusedDistance() {
      return this.focusedStore ? formatKm(this.distanceById[this.focusedStore.id]) : "";
    },
    // 시트나 상세가 열려 있으면 뒤 페이지 스크롤을 막는다 (푸터가 올라오거나 뒤 화면이 움직이지 않게)
    pageScrollLocked() {
      return this.sheetOpen || this.detailOpen;
    },
  },
  watch: {
    // 캐시로 먼저 그린 뒤 새 데이터가 들어오면 지도 핀도 다시 그린다
    storeRows() {
      this.renderMarkers();
      if (this.hasLocation && this.focusedId == null) this.focusNearest();
    },
    pageScrollLocked(locked) {
      if (locked) lockPageScroll();
      else unlockPageScroll();
    },
  },
  created() {
    // 지도 객체는 Vue가 추적하지 않도록 data 밖에 둔다 (지도 공통 부분은 storeMapMixin)
    this.hqMarker = null;
    // 뒤로가기로 닫을 레이어 순서 ("sheet" | "detail")
    this.layerStack = [];
  },
  async mounted() {
    this.setViewportHeight();
    window.addEventListener("resize", this.setViewportHeight);
    window.addEventListener("popstate", this.onPopState);
    document.addEventListener("click", this.closeTip);
    // 목록이 한 화면에 맞는 페이지라 위로가기 버튼은 숨긴다 (목록 오른쪽 아래 거리 표시를 가림)
    document.body.classList.add("find-store-page");
    this.dropStaleLayers();
    if (!this.storeRows.length) {
      await this.$store.dispatch("support/FETCH_STORE");
      if (!this.storeRows.length) this.loadError = true;
    }
    this.initMap();
    this.openInitialStore();
    this.initLocation();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setViewportHeight);
    window.removeEventListener("popstate", this.onPopState);
    document.removeEventListener("click", this.closeTip);
    document.body.classList.remove("find-store-page");
    if (this.pageScrollLocked) unlockPageScroll();
    this.hqMarker = null;
  },
  methods: {
    // 지도는 한 번만 만든다 (손 조작·핀·묶음은 storeMapMixin)
    initMap() {
      const nm = this.naverMaps();
      if (!this.createMap(HQ)) return;

      this.hqMarker = new nm.Marker({
        position: new nm.LatLng(HQ.lat, HQ.lng),
        map: this.map,
        icon: storeMarkerIcon(nm, HQ.label, true),
      });
      this.centerVisually(HQ.lat, HQ.lng, "store");
      this.renderMarkers();
    },

    // 위치를 아직 모를 때는 지도 가운데 큰 "내 위치" 버튼 자리를 비워둔다
    bottomReserve() {
      const view = isMobileView() ? "mobile" : "desktop";
      return MAP_BOTTOM_RESERVE[view][this.hasLocation ? "normal" : "withLargeButton"];
    },

    // 핀 그림·매장명 어디를 눌러도 목록을 누른 것처럼 선택 + 요약 시트
    onMarkerClick(item) {
      this.selectStore(item);
    },

    // 모바일 브라우저 주소창을 뺀 실제 화면 높이 (목록 높이 계산에 사용)
    setViewportHeight() {
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    },

    async initLocation() {
      const mock = getMockLocation(this.$route.query);
      if (mock) {
        this.applyLocation(mock.lat, mock.lng);
        return;
      }
      // 이미 거부된 상태면 팝업 없이 바로 검색 탭으로 (주변 탭이 잠깐 보였다 바뀌지 않게)
      if ((await locationPermissionState()) === "denied") {
        this.onLocationDenied();
        return;
      }
      this.requestLocation();
    },

    // userInitiated: "내 위치" 버튼으로 요청했는지 (그때만 안내창을 띄운다)
    requestLocation(userInitiated = false) {
      if (!navigator.geolocation) {
        this.onLocationDenied();
        if (userInitiated) alert("이 브라우저에서는 현재 위치를 확인할 수 없어요. 지역이나 매장명으로 검색해주세요.");
        return;
      }
      this.locating = true;
      navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.locating = false;
            this.applyLocation(pos.coords.latitude, pos.coords.longitude, { showBoth: userInitiated });
          },
          (err) => {
            // 거부·시간 초과·위치 확인 불가
            this.locating = false;
            this.onLocationDenied();
            if (userInitiated) alert(locationErrorMessage(err));
          },
          { timeout: 10000, maximumAge: 60000 }
      );
    },

    // 위치를 쓸 수 없으면 "검색해서 찾기"를 기본으로 연다
    onLocationDenied() {
      this.locationDenied = true;
      if (!this.hasLocation && !this.tabTouched) this.activeTab = "search";
    },

    // showBoth: 내 위치와 가장 가까운 매장이 한 화면에 함께 보이게 (버튼으로 찾았을 때)
    applyLocation(lat, lng, { showBoth = false } = {}) {
      this.nowLat = lat;
      this.nowLng = lng;
      this.locationDenied = false;
      this.drawMyLocation();
      const nearest = this.storesByDistance[0];
      if (!nearest) return;
      if (showBoth) {
        this.focusStore(nearest, { center: false });
        this.fitMyLocationAnd(nearest);
      } else if (this.focusedId == null) {
        // 위치 확인을 기다리는 사이 고객이 이미 매장을 골랐다면(또는 ?store= 링크) 그대로 둔다
        this.focusStore(nearest);
      }
    },

    // "내 위치" 버튼: 위치가 없으면 권한을 묻고, 있으면 내 위치와 가장 가까운 매장을 함께 보여준다
    onMyLocation() {
      if (this.locating) return;
      if (!this.hasLocation) {
        const mock = getMockLocation(this.$route.query);
        if (mock) {
          this.applyLocation(mock.lat, mock.lng, { showBoth: true });
          return;
        }
        this.requestLocation(true);
        return;
      }
      const nearest = this.storesByDistance[0];
      if (nearest) {
        this.focusStore(nearest, { center: false });
        this.fitMyLocationAnd(nearest);
      }
    },

    focusNearest() {
      const nearest = this.storesByDistance[0];
      if (nearest) this.focusStore(nearest);
    },

    // 그 매장 라벨만 선택 상태로 표시하고, center가 true면
    // 처음 확대 정도(17)로 돌아가 그 매장을 눈에 보이는 가운데에 둔다
    focusStore(item, { center = true } = {}) {
      if (!item) return;
      // 매장을 고르면 펼쳤던 묶음 표시는 끝내고 선택한 매장만 파란색으로
      this.clearHighlight();
      const nm = this.naverMaps();
      if (nm) {
        if (this.focusedId != null && this.focusedId !== item.id) {
          const prevItem = this.storeRows.find((s) => s.id === this.focusedId);
          const prevMarker = this.markerById[this.focusedId];
          if (prevItem && prevMarker) {
            prevMarker.setIcon(storeMarkerIcon(nm, prevItem.name, false));
            prevMarker.setZIndex(0);
          }
        }
        const marker = this.markerById[item.id];
        if (marker) {
          marker.setIcon(storeMarkerIcon(nm, item.name, true));
          marker.setZIndex(100);
        }
        // 매장을 고르면 본사 표시는 숨긴다 (같은 자리에 라벨이 겹치지 않게)
        if (this.hqMarker) this.hqMarker.setMap(null);
      }
      this.focusedId = item.id;
      if (nm && this.map) {
        if (center) {
          this.map.setZoom(MAP_ZOOM.initial);
          this.centerVisually(item.lat, item.lng, "store");
        }
        this.updateMarkerVisibility();
      }
    },

    // 목록·지도 핀을 누르면: 처음 확대 정도로 그 매장을 보여주고 요약 시트를 연다
    // pushHistory: 뒤로가기로 시트가 닫히게 방문 기록을 한 칸 쌓을지 (?store= 링크로 열 때는 쌓지 않음)
    selectStore(item, { pushHistory = true } = {}) {
      if (!item) return;
      this.focusStore(item);
      if (this.sheetOpen) return;
      // 페이지가 내려가 있으면 시트 아래 버튼이 화면 밖에 있으므로 맨 위로 올린 뒤 연다
      if (window.pageYOffset > 0) window.scrollTo(0, 0);
      this.sheetOpen = true;
      if (pushHistory) this.pushLayer("sheet");
    },

    // 매장찾기: ?store=매장코드 / 신청서: 이미 고른 매장 → 그 매장을 고른 상태로 연다
    openInitialStore() {
      const code = this.isPicker ? this.initialStoreCode : this.$route.query.store;
      if (!code) return;
      const item = this.storeRows.find((s) => s.strCode === String(code));
      if (!item) return;
      this.selectStore(item, { pushHistory: false });
      // 시트를 닫으면 바로 그 매장 줄이 보이도록 목록도 미리 내려둔다
      this.scrollListToFocused();
    },

    // 이미 고른 매장이 목록 아래쪽에 있어도 보이도록 그 줄로 목록을 내린다
    scrollListToFocused() {
      this.$nextTick(() => {
        const row = this.$el.querySelector(".find-item.display .find-result-item.is-selected");
        if (row && row.scrollIntoView) row.scrollIntoView({ block: "center" });
      });
    },

    openDetail(item) {
      this.detailRenderKey += 1;
      this.storeProps = toDetailProps(item);
      if (this.detailOpen) return;
      this.detailOpen = true;
      this.pushLayer("detail");
    },

    // 뒤로가기(폰 뒤로 버튼·밀어서 뒤로 포함)로 상세 → 시트 → 목록 순서로 닫히도록 방문 기록에 한 칸씩 쌓는다
    // 주소는 그대로이고, 기록에 몇 칸째인지만 남긴다
    pushLayer(name) {
      this.layerStack.push(name);
      history.pushState({ ...history.state, findStoreLayers: this.layerStack.length }, "");
    },

    // 화면의 X·"목록으로" 버튼: 기록을 쌓은 레이어면 뒤로가기와 똑같이, 아니면 바로 닫는다
    backOrClose(name) {
      if (this.layerStack[this.layerStack.length - 1] === name) {
        history.back();
        return;
      }
      this.closeLayer(name);
    },

    onPopState() {
      const depth = (history.state && history.state.findStoreLayers) || 0;
      while (this.layerStack.length > depth) this.closeLayer(this.layerStack.pop());
    },

    closeLayer(name) {
      if (name === "detail") this.detailOpen = false;
      if (name === "sheet") this.sheetOpen = false;
    },

    // 시트·상세가 열린 채 다른 페이지로 갔다가 뒤로 돌아오거나 새로고침하면,
    // 그때 쌓인 기록을 걷어낸다 (같은 주소라 화면 변화 없음 → 다음 뒤로가기 한 번에 이전 페이지로)
    dropStaleLayers() {
      const stale = (history.state && history.state.findStoreLayers) || 0;
      if (stale > 0) history.go(-stale);
    },

    changeTab(tab) {
      this.tabTouched = true;
      this.activeTab = tab;
    },

    // 엔터·돋보기: 키보드를 닫고 첫 번째 결과로 지도 이동
    // 한글은 조합(IME) 중에도 엔터가 눌린다. 그 엔터는 "글자 확정"용이라
    // 여기서 blur 해버리면 조합이 깨져 마지막 글자가 한 번 더 들어간다(성북구 → 성북구구).
    // 그래서 조합이 끝난 뒤(keyup)에 처리하고, 아직 조합 중이면 그냥 넘긴다.
    onSearchEnter(e) {
      if (e && e.isComposing) return;
      this.commitSearch();
    },

    commitSearch() {
      // 조합 중에 blur 하면 IME 가 글자를 한 번 더 확정해 버린다 → 그때는 키보드를 내리지 않는다
      if (!this.searchComposing && this.$refs.searchInput) this.$refs.searchInput.blur();
      const first = this.searchList[0];
      if (first) this.focusStore(first);
    },

    closeTip() {
      this.tipOpen = false;
    },

    // 신청서에서 고를 때 [이 매장 선택]
    pick(item) {
      if (item) this.$emit("select", item);
    },

    consultLink(item) {
      return consultationLink(item && item.strCode);
    },

    copyAddress(text) {
      copyAddress(text);
    },
  },
};
</script>
<style>
  #detail-section {
    overflow: hidden;
  }
  .m-find-store + footer {
    margin-top: 0 !important;
  }
  /* 매장찾기에서는 위로가기 버튼 숨김 (목록·시트 아래 버튼을 가리지 않게) */
  body.find-store-page #goToTop {
    display: none !important;
  }

  @keyframes floatUpDown {
    0%, 100% {
      transform: translateX(-35%) translateY(0);
    }
    50% {
      transform: translateX(-35%) translateY(-3px);
    }
  }

  #map .marker .marker-txt{
    position: absolute;
    margin-top: 14px;
    box-sizing: content-box;
    text-align: center;
    top: -38px;
    white-space: nowrap;
    min-width: 100px;
    left: 50%;
    transform: translateX(-35%);
    padding: 15px 20px;
    background-color: #fff;
    color: #000;
    border-radius: 30px;
    margin-bottom: 10px;
    max-width: none;
    max-height: none;
    box-shadow: 0 2px 5px 0 rgba(34, 34, 34, .1);
    font-weight: 500;
  }
  #map .marker .marker-txt.active{
    background-color: #0056AAFF;
    color: #fff;
    cursor: pointer;
    box-shadow: 0 4px 12px 0 rgba(0, 86, 170, .35);
    animation: floatUpDown 2s ease-in-out infinite;
  }
  #map .marker .marker-txt.active:hover{
    box-shadow: 0 6px 16px 0 rgba(0, 86, 170, .45);
    animation-play-state: paused;
    transform: translateX(-35%) translateY(-2px);
  }

  /* 모바일: 지도 라벨을 작게, 핀 바로 위 가운데에 (오른쪽 +/− 버튼과 겹치지 않게) */
  @keyframes floatUpDownCenter {
    0%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(-2px);
    }
  }
  @media screen and (max-width: 768px) {
    #map .marker .marker-txt {
      top: -14px;
      margin-top: 0;
      left: 22.5px; /* 핀 이미지(45px) 가운데 */
      transform: translateX(-50%);
      min-width: 0;
      padding: 6px 12px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 18px;
    }
    #map .marker .marker-txt.active {
      box-shadow: 0 3px 8px 0 rgba(0, 86, 170, .3);
      animation-name: floatUpDownCenter;
    }
    #map .marker .marker-txt.active:hover {
      box-shadow: 0 3px 8px 0 rgba(0, 86, 170, .3);
      transform: translateX(-50%) translateY(-2px);
    }
  }

  /* 멀리서 볼 때 가까이 붙은 매장 묶음 (기존 파란색 톤, 누르면 확대) */
  #map .store-cluster {
    box-sizing: border-box;
    border: 3px solid #fff;
    border-radius: 50%;
    background-color: rgba(0, 86, 170, .9);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
    cursor: pointer;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, .25);
  }

  /* 멀리서 볼 때는 선택한 매장 외 이름을 숨기고 핀만 (이름이 겹치지 않게) */
  .map-wrap.is-far #map .marker .marker-txt:not(.active) {
    display: none;
  }

  /* 내 위치: 매장 라벨과 같은 둥근 모양, 흰 바탕 + 파란 테두리 + 파란 점 */
  #map .my-location {
    position: relative;
    width: 80px;
    height: 44px;
  }
  #map .my-location .my-location-txt {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: 3px 10px;
    border: 2px solid #0056AA;
    border-radius: 14px;
    background-color: #fff;
    color: #0056AA;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    white-space: nowrap;
    box-shadow: 0 2px 5px 0 rgba(34, 34, 34, .15);
  }
  #map .my-location .my-location-dot {
    position: absolute;
    top: 30px;
    left: 50%;
    width: 14px;
    height: 14px;
    margin-left: -7px;
    border: 3px solid #fff;
    border-radius: 50%;
    background-color: #0056AA;
    box-shadow: 0 0 0 6px rgba(0, 86, 170, .2);
  }
</style>
<style lang="scss" scoped>
@import "~@/assets/scss/helpers/variables";
@import "~@/assets/scss/helpers/mixins";
@import "~@/assets/scss/base/typhography";

//전체 레이아웃
.m-find-store {
  max-width: 100vw;
  overflow: hidden;
  // 지도 높이 (목록 높이도 이 값으로 계산)
  // PC: 기존 비율 그대로 / 폰: 화면 높이의 30% (200~280px) — 핀·라벨·버튼이 겹치지 않고 목록은 3곳 이상 보이게
  --store-map-h: min(500px, max(150px, calc(45vh - var(--header-height, 64px))));
  @media screen and (max-width: 768px) {
    --store-map-h: clamp(200px, 30vh, 280px);
  }
  .find-store-cont {
    width: 100%;
    //매장찾기 검색
    .find-section {
      // 요약 시트를 이 영역(탭+목록) 위에 겹쳐 띄우는 기준.
      // z-index 0: 헤더·메뉴(1000~1200)보다 위로 올라가지 않게 (예전 99999999는 position이 없어 적용되지 않던 값)
      position: relative;
      z-index: 0;
      width: 100%;
      max-width: 768px;
      background-color: $color-white;
      // 주변에서 찾기, 검색해서 찾기
      //버튼
      .find-btns {
        width: 100%;
        border-bottom: 1px solid #dedede;
        .find-btn {
          display: inline-block;
          width: 50%;
          height: 50px;
          line-height: 50px;
          text-align: center;
          &.active button {
            background-color: $color-blue;
            color: $color-white;
          }
          button {
            font-size: 15px;
            font-weight: 500;
            letter-spacing: -0.45px;
            display: block;
            width: 100%;
            height: 100%;
            @media screen and (max-width: 768px) {
              font-size: 16px;
            }
          }
        }
      }
      //공통부분 레이아웃
      .find-items {
        .find-item {
          height: 100%;
          overflow: hidden;
          display: none;
          &.display {
            display: block;
          }
          // 총--개
          .find-result-comment {
            height: 50px;
            justify-content: center;
            align-items: center;
            line-height: 50px;
            text-align: center;
            border-bottom: 1px solid #dedede;
            p {
              font-size: 15px;
              letter-spacing: -0.45px;
              em {
                color: $color-blue;
              }
            }
            .sort-label {
              color: #777;
              margin-left: 2px;
            }
            // "가까운 순 ?" : 누르거나(폰) 올리면(PC) "직선거리 기준입니다"
            .sort-help {
              position: relative;
              display: inline-block;
              min-width: 0;
              min-height: 0;
              width: 18px;
              height: 18px;
              margin-left: 2px;
              border: 1px solid #aaa;
              border-radius: 50%;
              font-size: 12px;
              line-height: 16px;
              color: #777;
              vertical-align: 1px;
              // 누르기 쉽게 터치 영역을 약 44px로 넓힌다
              &::before {
                content: "";
                position: absolute;
                top: -13px;
                right: -13px;
                bottom: -13px;
                left: -13px;
              }
              &:hover .sort-tip {
                display: block;
              }
            }
            .sort-tip {
              display: none;
              position: absolute;
              top: 26px;
              right: -10px;
              z-index: 10;
              padding: 6px 10px;
              border-radius: 4px;
              background-color: #222;
              color: #fff;
              font-size: 13px;
              line-height: 1.4;
              letter-spacing: 0;
              white-space: nowrap;
              &.show {
                display: block;
              }
            }
            // 위치 허용 안내: "가까운 순"과 같은 모양으로 한 줄 (아주 좁은 화면에서는 말줄임)
            .sort-label.is-hint {
              display: block;
              margin-left: 0;
              padding: 0 16px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            // 불러오기 실패 문구는 폰에서 두 줄이 될 수 있어 줄 간격을 줄여 50px 안에 맞춘다
            &.is-message p {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              padding: 0 16px;
              line-height: 20px;
              word-break: keep-all;
            }
          }
          //내용 상세
          // 화면 높이 - 헤더 - 지도 - 탭(51) - 안내줄(50) [- 검색창(50)] : 목록이 화면 밖으로 넘치지 않게
          .find-result-wrap {
            overflow: hidden;
            height: calc(var(--vh, 1vh) * 100 - var(--header-height, 64px) - var(--store-map-h) - var(--store-list-offset, 101px));
            min-height: 160px;
            @media screen and (max-width: 768px) {
              height: calc(var(--vh, 1vh) * 100 - var(--header-height, 56px) - var(--store-map-h) - var(--store-list-offset, 101px));
            }
            .find-result {
              overflow-y: auto;
              // 목록 끝까지 스크롤해도 뒤 페이지가 따라 움직이지 않게
              overscroll-behavior: contain;
              height: 100%;
              .find-result-items {
                flex: 1;
                @include scroll-bar;
              }
            }
          }
          //검색창
          &.search {
            --store-list-offset: 151px;
            .search-input {
              position: relative;
              background-color: #f4f5f8;
              width: 100%;
              height: 50px;
              input {
                display: block;
                width: 100%;
                height: 100%;
                padding: 0 30px 0 20px;
                // 16px 미만이면 아이폰이 입력할 때 화면을 확대하므로 입력 글자·안내 글자 모두 16px
                font-size: 16px;
                &::placeholder {
                  color: #c2c2c4;
                  @include font-main;
                  font-size: 16px;
                }
              }
              img {
                position: absolute;
                bottom: 11px;
                right: 15px;
              }
            }
          }
        }
      }
    }
    // 지도 (지도 위 버튼은 .map-wrap 기준으로 배치)
    .map-wrap {
      position: relative;
      margin-top: var(--header-height, 64px);
      @media screen and (max-width: 768px) {
        margin-top: var(--header-height, 56px);
      }
    }
    .map-section {
      // 헤더 높이 값이 늦게 정해져도 지도 크기는 ResizeObserver가 다시 맞춘다
      height: var(--store-map-h);
      .marker {
        display: inline-block;
        width: auto;
        transform: translate(0, 0);
        img {
          display: block;
          width: auto;
          margin: auto;
        }
      }
    }
    // +/− 확대·축소 (오른쪽 위)
    .map-zoom {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 10;
      display: flex;
      flex-direction: column;
      border: 1px solid #dedede;
      background-color: $color-white;
      box-shadow: 0 2px 5px 0 rgba(34, 34, 34, 0.1);
      button {
        // 사이트 공통 버튼 최소 크기(48px) 대신 44px 정사각형
        min-width: 0;
        min-height: 0;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        line-height: 1;
        color: #222;
        & + button {
          border-top: 1px solid #dedede;
        }
      }
    }
    // 내 위치 버튼 (위치 있을 때: 왼쪽 아래 작게 / 없을 때: 가운데 크게)
    .my-location-btn {
      position: absolute;
      left: 8px;
      bottom: 26px; // 네이버 로고·저작권 표시 위
      z-index: 10;
      display: flex;
      align-items: center;
      min-width: 0;
      min-height: 0;
      height: 36px;
      padding: 0 12px;
      border: 1px solid $color-blue;
      border-radius: 18px;
      background-color: $color-white;
      box-shadow: 0 2px 5px 0 rgba(34, 34, 34, 0.15);
      color: $color-blue;
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
      white-space: nowrap;
      &.is-large {
        left: 0;
        right: 0;
        width: max-content;
        max-width: calc(100% - 120px);
        margin: 0 auto;
        span {
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      &:disabled {
        opacity: 0.7;
      }
      .my-location-icon {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        margin-right: 5px;
        fill: $color-blue;
        stroke: $color-blue;
        stroke-width: 2;
        circle + circle {
          fill: none;
        }
      }
      // 모바일은 한 단계 작게 (글자는 어르신도 읽기 쉬운 14px 유지)
      @media screen and (max-width: 768px) {
        height: 32px;
        padding: 0 12px;
        border-radius: 16px;
        .my-location-icon {
          width: 15px;
          height: 15px;
          margin-right: 4px;
        }
      }
      @media screen and (max-width: 360px) {
        font-size: 13px;
        padding: 0 10px;
      }
    }
  }

  // 매장 요약 시트: 탭+목록 자리를 덮고, 지도는 위에 그대로 보인다
  .store-sheet {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    background-color: $color-white;
    border-top: 1px solid #dedede;
    box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.08);
    // "< 목록으로": 폭 전체를 누를 수 있게 (뒤로가기와 같은 동작)
    .store-sheet-back {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      width: 100%;
      min-width: 0;
      min-height: 0;
      height: 48px;
      padding: 0 16px;
      border-bottom: 1px solid #dedede;
      font-size: 16px;
      font-weight: 500;
      color: #222;
      text-align: left;
      .store-sheet-back-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        margin-right: 4px;
        fill: none;
        stroke: #222;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      // 신청서에서 고르는 중: 다른 매장을 고르러 가는 길이 눈에 띄게
      &.is-change {
        color: $color-blue;
        font-weight: 600;
        .store-sheet-back-icon {
          stroke: $color-blue;
        }
      }
    }
    .store-sheet-body {
      flex: 1;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 0 20px 16px;
    }
    .store-sheet-title {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      padding: 18px 0 10px;
      h3 {
        margin-right: 12px;
        font-weight: bold;
        font-size: 20px;
        line-height: 28px;
        letter-spacing: -0.3px;
        color: #222;
        word-break: keep-all;
      }
      .distance {
        flex-shrink: 0;
        font-size: 15px;
        font-weight: 500;
        color: $color-blue;
      }
    }
    // 사진·상세 정보 보기: 검색창과 같은 연회색 바탕, 테두리 없이
    .store-sheet-more {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 0;
      height: 48px;
      margin-top: 14px;
      background-color: #f4f5f8;
      font-size: 15px;
      font-weight: 500;
      color: #222;
      .store-sheet-more-icon {
        width: 16px;
        height: 16px;
        margin-left: 2px;
        fill: none;
        stroke: #222;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    }
    // 상담예약·방문예약: 상세 화면 버튼 그대로, 시트 맨 아래에 붙인다
    .kakao-reserv-btns {
      position: static;
      top: auto;
      flex-shrink: 0;
    }
  }
  // 세부 정보
  .detail-section {
    height: 100vh;
    padding: 20px;
    margin: 0 auto;
    background-color: $color-white;
    position: fixed;
    top: 0;
    right: 50%;
    transform: translateX(50%);
    z-index: 999999999999;
    width: 100%;
    max-width: 500px;
    @media (max-width: 768px) {
      max-width: 768px;
    }
    &.out {
      right: -100vw;
    }
    // 숨기기 버튼
    // 닫기: 누르는 영역 44px (위 흰 줄 52px 안, X 끝이 사진 오른쪽 끝과 맞게)
    .detail-btn {
      position: absolute;
      width: 44px;
      height: 44px;
      top: 4px;
      right: 8px;
      @include box-center-flex;
      img {
        transform: rotate(90deg);
      }
    }
  }
}
</style>
